
import React, { useEffect, useRef } from 'react';
import { useAlerts } from '../../contexts/AlertContext';
import { useNavigate } from 'react-router-dom';

const AlertBell: React.FC = () => {
  const { alerts, unackedCount, acknowledge, isBellOpen, setIsBellOpen } = useAlerts();
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close on click outside
  useEffect(() => {
    if (!isBellOpen) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsBellOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isBellOpen, setIsBellOpen]);

  if (alerts.length === 0) return null;

  return (
    <div className="relative" ref={panelRef}>
      {/* ── Bell Button ── */}
      <button
        onClick={() => setIsBellOpen(!isBellOpen)}
        className="relative flex items-center justify-center w-9 h-9 rounded-full hover:bg-dark-700 transition-colors"
        aria-label={`Notifications${unackedCount > 0 ? ` — ${unackedCount} unread` : ''}`}
      >
        <span className="text-xl">🔔</span>
        {unackedCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-amber-500 text-black text-xs font-bold rounded-full flex items-center justify-center leading-none">
            {unackedCount}
          </span>
        )}
      </button>

      {/* ── Dropdown Panel ── */}
      {isBellOpen && (
        <div className="absolute right-0 top-11 w-80 bg-dark-800 border border-dark-600 rounded-xl shadow-2xl z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-dark-700">
            <h3 className="text-sm font-semibold text-white">Notifications</h3>
            {unackedCount > 0 && (
              <span className="text-xs text-amber-400">{unackedCount} unread</span>
            )}
          </div>

          {/* Alert list */}
          <div className="max-h-96 overflow-y-auto divide-y divide-dark-700">
            {alerts.map((alert) => (
              <div
                key={alert.alertId}
                className={`px-4 py-3 transition-colors ${
                  alert.acknowledged ? 'opacity-50' : 'bg-amber-900/10'
                }`}
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    {/* Urgency / type badge */}
                    {!alert.acknowledged && (
                      alert.type === 'TrainingPlanAssigned' ? (
                        <span className="inline-block text-xs font-bold px-1.5 py-0.5 rounded mb-1 bg-green-900/50 text-green-400">
                          NEW PLAN
                        </span>
                      ) : alert.type === 'TrainingPlanUpdated' ? (
                        <span className="inline-block text-xs font-bold px-1.5 py-0.5 rounded mb-1 bg-indigo-900/50 text-indigo-400">
                          PLAN UPDATED
                        </span>
                      ) : alert.type === 'PlanFileShared' ? (
                        <span className="inline-block text-xs font-bold px-1.5 py-0.5 rounded mb-1 bg-teal-900/50 text-teal-400">
                          PLAN FILE
                        </span>
                      ) : alert.type === 'NewMessage' ? (
                        <span className="inline-block text-xs font-bold px-1.5 py-0.5 rounded mb-1 bg-blue-900/50 text-blue-400">
                          MESSAGE
                        </span>
                      ) : alert.type === 'DailyCheckInReminder' ? (
                        <span className="inline-block text-xs font-bold px-1.5 py-0.5 rounded mb-1 bg-blue-900/50 text-blue-400">
                          TODAY
                        </span>
                      ) : alert.daysUntilStart !== undefined ? (
                        <span className={`inline-block text-xs font-bold px-1.5 py-0.5 rounded mb-1 ${
                          alert.daysUntilStart <= 7
                            ? 'bg-red-900/50 text-red-400'
                            : 'bg-amber-900/50 text-amber-400'
                        }`}>
                          {alert.daysUntilStart === 0 ? 'TODAY' : `${alert.daysUntilStart}d`}
                        </span>
                      ) : null
                    )}
                    <p className={`text-sm font-medium truncate ${alert.acknowledged ? 'text-gray-500' : 'text-white'}`}>
                      {alert.title}
                    </p>
                    <p className={`text-xs mt-0.5 line-clamp-2 ${alert.acknowledged ? 'text-gray-600' : 'text-gray-400'}`}>
                      {alert.body}
                    </p>
                  </div>

                  {/* Action buttons */}
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    {/* Training alerts: navigate to /training and ack */}
                    {(alert.type === 'TrainingPlanAssigned' || alert.type === 'TrainingPlanUpdated') && (
                      <>
                        <button
                          onClick={() => {
                            acknowledge(alert.alertId);
                            setIsBellOpen(false);
                            navigate('/training');
                          }}
                          className="text-xs text-blue-400 hover:text-blue-300 whitespace-nowrap"
                        >
                          View plan →
                        </button>
                        <button
                          onClick={() => acknowledge(alert.alertId)}
                          className="text-xs text-green-400 hover:text-green-300 whitespace-nowrap"
                          title="Mark as acknowledged"
                        >
                          ✓ Ack
                        </button>
                      </>
                    )}

                    {/* Plan file alert: navigate to Dashboard (Plan Files section) and ack */}
                    {alert.type === 'PlanFileShared' && (
                      <>
                        <button
                          onClick={() => {
                            acknowledge(alert.alertId);
                            setIsBellOpen(false);
                            navigate('/');
                          }}
                          className="text-xs text-teal-400 hover:text-teal-300 whitespace-nowrap"
                        >
                          View file →
                        </button>
                        <button
                          onClick={() => acknowledge(alert.alertId)}
                          className="text-xs text-green-400 hover:text-green-300 whitespace-nowrap"
                          title="Mark as acknowledged"
                        >
                          ✓ Ack
                        </button>
                      </>
                    )}

                    {/* NewMessage: deep-link to the relevant page */}
                    {alert.type === 'NewMessage' && (
                      <button
                        onClick={() => {
                          setIsBellOpen(false);
                          let url = '/messages';
                          if (alert.contextType === 'TrainingSession' && alert.contextId) {
                            if (!alert.senderIsCoach && alert.senderUserId && alert.senderName && alert.relationshipId) {
                              // Coach receiving athlete reply → athlete's training in coach-view mode
                              const params = new URLSearchParams({
                                sessionId: alert.contextId,
                                openCoachNotes: 'true',
                                viewAs: alert.senderUserId,
                                viewAsName: alert.senderName,
                                relationshipId: alert.relationshipId,
                              });
                              url = `/training?${params}`;
                            } else {
                              // Athlete receiving a coach note → own training page
                              const params = new URLSearchParams({ sessionId: alert.contextId, openCoachNotes: 'true' });
                              url = `/training?${params}`;
                            }
                          } else if (alert.contextType === 'DailyCheckIn' || alert.contextType === 'PEDCycle') {
                            const basePage = alert.contextType === 'DailyCheckIn' ? '/metrics' : '/ped-tracking';
                            if (alert.senderIsCoach) {
                              // Athlete receiving a coach note → own page, ?open= auto-expands that thread
                              url = alert.contextId ? `${basePage}?open=${alert.contextId}` : basePage;
                            } else if (alert.senderUserId && alert.senderName && alert.relationshipId) {
                              // Coach receiving an athlete reply → athlete's page in coach view
                              // Include contextId as ?open= so the detail drawer auto-expands
                              const params = new URLSearchParams({
                                viewAs: alert.senderUserId,
                                viewAsName: alert.senderName,
                                relationshipId: alert.relationshipId,
                                ...(alert.contextType === 'DailyCheckIn' && alert.contextId
                                  ? { open: alert.contextId }
                                  : {}),
                              });
                              url = `${basePage}?${params}`;
                            } else {
                              url = basePage;
                            }
                          } else if (alert.relationshipId) {
                            url = `/messages?rel=${alert.relationshipId}`;
                          }
                          navigate(url);
                        }}
                        className="text-xs text-blue-400 hover:text-blue-300 whitespace-nowrap"
                      >
                        {alert.contextType === 'TrainingSession' ? 'View session →'
                          : alert.contextType && alert.contextType !== 'General' ? 'View note →'
                          : 'Open →'}
                      </button>
                    )}

                    {/* DailyCheckIn / PED order: ack + navigate */}
                    {alert.type !== 'NewMessage'
                      && alert.type !== 'TrainingPlanAssigned'
                      && alert.type !== 'TrainingPlanUpdated'
                      && alert.type !== 'PlanFileShared'
                      && !alert.acknowledged && (
                      <button
                        onClick={() => acknowledge(alert.alertId)}
                        className="text-xs text-green-400 hover:text-green-300 whitespace-nowrap"
                        title="Mark as acknowledged"
                      >
                        ✓ Ack
                      </button>
                    )}
                    {(alert.type === 'DailyCheckInReminder' || alert.cycleId) && (
                      <button
                        onClick={() => {
                          setIsBellOpen(false);
                          navigate(
                            alert.type === 'DailyCheckInReminder'
                              ? '/metrics/entry'
                              : '/ped-tracking'
                          );
                        }}
                        className="text-xs text-blue-400 hover:text-blue-300 whitespace-nowrap"
                      >
                        Log now →
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          {alerts.length > 0 && unackedCount === 0 && (
            <div className="px-4 py-2 text-center text-xs text-gray-600 border-t border-dark-700">
              All caught up ✓
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AlertBell;
